export const DEFAULT_TRANSLATION_LANGUAGES = ["de", "en"] as const;

const MEANINGFUL_HTML_TAG =
  /<(img|table|iframe|video|audio|svg|embed|object|hr|canvas)\b/i;

export function isTranslationValueEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  if (MEANINGFUL_HTML_TAG.test(value)) {
    return false;
  }

  const withoutMarkup = value
    .replace(/<br\s*[^>]*>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();

  return withoutMarkup.length === 0;
}

export function isTranslationFieldEmpty(
  translations: Record<string, unknown> | undefined,
  languages: readonly string[]
): boolean {
  return languages.every((language) =>
    isTranslationValueEmpty(translations?.[language])
  );
}

export function omitEmptyTranslationFields(
  data: Record<string, unknown>,
  fieldNames: readonly string[],
  languages: readonly string[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  fieldNames.forEach((fieldName) => {
    const translations = data[fieldName];
    if (!translations || typeof translations !== "object") {
      return;
    }

    const translationValues = translations as Record<string, unknown>;
    if (isTranslationFieldEmpty(translationValues, languages)) {
      return;
    }

    result[fieldName] = translationValues;
  });

  return result;
}

export function collectTranslationLanguages(
  data: Record<string, unknown> | undefined,
  fieldNames: readonly string[]
): string[] {
  const languages = new Set<string>(DEFAULT_TRANSLATION_LANGUAGES);

  fieldNames.forEach((fieldName) => {
    const value = data?.[fieldName];
    if (typeof value !== "object" || value === null) {
      return;
    }

    Object.keys(value).forEach((language) => languages.add(language));
  });

  return [
    ...DEFAULT_TRANSLATION_LANGUAGES,
    ...Array.from(languages).filter(
      (language) => language !== "de" && language !== "en"
    ),
  ];
}

export function normalizeTranslationPhrase(value: unknown): string {
  if (typeof value !== "string" || isTranslationValueEmpty(value)) {
    return "";
  }
  return value.trim().toLowerCase();
}

type TranslationPhraseMap = Record<
  string,
  Record<string, Record<string, Set<string>>>
>;

export function buildTranslationPhraseMap(
  values: Record<string, unknown>
): TranslationPhraseMap {
  const langPhraseMap: TranslationPhraseMap = {};

  Object.values(values).forEach((translations) => {
    if (!translations || typeof translations !== "object") {
      return;
    }

    const translationValues = translations as Record<string, unknown>;
    Object.entries(translationValues).forEach(([sourceLang, sourceValue]) => {
      const sourcePhrase = normalizeTranslationPhrase(sourceValue);
      if (!sourcePhrase) {
        return;
      }

      if (!langPhraseMap[sourceLang]) {
        langPhraseMap[sourceLang] = {};
      }
      if (!langPhraseMap[sourceLang][sourcePhrase]) {
        langPhraseMap[sourceLang][sourcePhrase] = {};
      }

      Object.entries(translationValues).forEach(([targetLang, targetValue]) => {
        if (targetLang === sourceLang) {
          return;
        }
        const targetPhrase = normalizeTranslationPhrase(targetValue);
        if (!targetPhrase) {
          return;
        }

        if (!langPhraseMap[sourceLang][sourcePhrase][targetLang]) {
          langPhraseMap[sourceLang][sourcePhrase][targetLang] = new Set();
        }
        langPhraseMap[sourceLang][sourcePhrase][targetLang].add(targetPhrase);
      });
    });
  });

  return langPhraseMap;
}

export function getDuplicateTranslationTarget(
  fieldName: string,
  language: string,
  values: Record<string, unknown>,
  phraseMap: TranslationPhraseMap = buildTranslationPhraseMap(values)
): { sourceLanguage: string; phrase: string } | null {
  const translations = values[fieldName];
  if (!translations || typeof translations !== "object") {
    return null;
  }

  const translationValues = translations as Record<string, unknown>;
  for (const sourceLang of Object.keys(translationValues)) {
    if (sourceLang === language) {
      continue;
    }

    const sourcePhrase = normalizeTranslationPhrase(
      translationValues[sourceLang]
    );
    if (!sourcePhrase) {
      continue;
    }

    const uniqueTranslations =
      phraseMap[sourceLang]?.[sourcePhrase]?.[language];
    if (uniqueTranslations && uniqueTranslations.size > 1) {
      return { sourceLanguage: sourceLang, phrase: sourcePhrase };
    }
  }

  return null;
}
